import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import SEO from '../../shared/components/SEO';
import {
  Box, Typography, alpha, AppBar, Toolbar, Button, IconButton, Drawer, List, ListItem, ListItemText,
  Menu, MenuItem, Avatar, Divider, Snackbar, Alert, Link,
  Dialog, CircularProgress, Fade, Grow, Chip, TextField,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import CloseIcon from '@mui/icons-material/Close';
import {
  SearchIcon,
  GridViewIcon,
  AutoAwesomeIcon,
  TrendingUpIcon,
  FileCopyIcon,
  CheckCircleIcon,
  LockIcon,
  ShieldIcon,
  VerifiedUserIcon,
  CloudDoneIcon,
  LogoutIcon,
  SettingsIcon,
  ArrowUpwardIcon,
  ArrowBackIcon,
} from '../../shared/icons';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import ChatBubbleRounded from '@mui/icons-material/ChatBubbleRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import PeopleRounded from '@mui/icons-material/PeopleRounded';
import InventoryRounded from '@mui/icons-material/InventoryRounded';
import LocalShippingRounded from '@mui/icons-material/LocalShippingRounded';
import PolicyRounded from '@mui/icons-material/PolicyRounded';
import EditNoteRounded from '@mui/icons-material/EditNoteRounded';
import SlideshowRounded from '@mui/icons-material/SlideshowRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import AutorenewRounded from '@mui/icons-material/AutorenewRounded';
import ShieldRounded from '@mui/icons-material/ShieldRounded';
// Use case library icons (P0)
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import PieChartRounded from '@mui/icons-material/PieChartRounded';
import CompareArrowsRounded from '@mui/icons-material/CompareArrowsRounded';
import GavelRounded from '@mui/icons-material/GavelRounded';
import MemoryRounded from '@mui/icons-material/MemoryRounded';
import ArticleRounded from '@mui/icons-material/ArticleRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import NotificationsRounded from '@mui/icons-material/NotificationsRounded';
import PublicRounded from '@mui/icons-material/PublicRounded';
import ListAltRounded from '@mui/icons-material/ListAltRounded';
import QueryStatsRounded from '@mui/icons-material/QueryStatsRounded';
// Integrations + compliance icons (P2 + P4)
import CloudQueueRounded from '@mui/icons-material/CloudQueueRounded';
import AssessmentRounded from '@mui/icons-material/AssessmentRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import FolderSharedRounded from '@mui/icons-material/FolderSharedRounded';
import HubRounded from '@mui/icons-material/HubRounded';
import ShowChartRounded from '@mui/icons-material/ShowChartRounded';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import PaidRounded from '@mui/icons-material/PaidRounded';
import LockOpenRounded from '@mui/icons-material/LockOpenRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import TimerRounded from '@mui/icons-material/TimerRounded';
import BoltRounded from '@mui/icons-material/Bolt';
import BlockRounded from '@mui/icons-material/BlockRounded';
import AppsRounded from '@mui/icons-material/AppsRounded';
import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import { brandConfig, BRAND_SANS_FONT } from '../../shared/design/theme';
import { useUser, useAuth } from '../../shared/contexts/UserContext';
import { SiteHeader } from '../../shared/components/SiteHeader';
import {
  FAQ_REPOSITORY,
  FAQ_FALLBACK_ANSWER,
  findFAQAnswer,
} from '../../shared/services/faqChatService';
import { db } from '../../shared/services/firebase';
import './HomePage.styles.css';
import { Footer } from '../../shared/components/Footer';
import { capture } from '../../shared/services/posthog';
import BlogPostModal from '../blog/BlogPostModal';
import CapabilityShowcase from './CapabilityShowcase';
import { useScrollReveal } from '../../shared/components/useScrollReveal';
import OfficeHeroStage from '../../shared/components/OfficeHeroStage';
import HeroDecoration from '../../shared/components/HeroDecoration';
import { blogPosts } from '../blog/BlogPostPage';

import {
  LandingContainer,
  Section,
  Container,
  SectionEyebrow,
  SectionTitle,
  SectionSubtitle,
  CenteredHeader,
  AnimateOnScroll,
  // Framework diagrams
  PorterDiagram,
  PorterCenter,
  PorterForce,
  PorterLine,
  SwotGrid,
  SwotCell,
  SwotLetter,
  SwotLabel,
  BcgGrid,
  BcgCell,
  BcgIcon,
  BcgLabel,
  TamDiagram,
  TamCircle,
  TamLabel,
  SevensGrid,
  SevensRow,
  SevensItem,
  ValueChainDiagram,
  ValueChainStep,
  ValueChainIcon,
  ValueChainLabel,
  // Capabilities
  CapabilitiesBento,
  BentoCard,
  BentoIcon,
  BentoTitle,
  BentoTitleRow,
  BentoUpcomingBadge,
  BentoDesc,
  BentoTags,
  BentoTag,
  BentoQuote,
  BentoExport,
  ExportBtn,
  // Capability visualizations (vendor-style proof visuals)
  CapVisualFrame,
  // Citations
  CitationTrace,
  CitationQuote,
  CitationChip,
  CitationConnector,
  CitationSource,
  CitationSourceIcon,
  CitationSourceBody,
  CitationSourceTitle,
  CitationSourceMeta,
  CitationSourceHighlight,
  // Governance
  GovCompare,
  GovPanel,
  GovPanelLabel,
  GovHeadline,
  GovSwatchRow,
  GovSwatch,
  GovArrow,
  GovChecklist,
  GovCheck,
  VaultStack,
  VaultCard,
  VaultLock,
  VaultBody,
  VaultName,
  VaultMeta,
  VaultIsolationLine,
  VaultBadges,
  VaultBadge,
  ConnectorOrbit,
  ConnectorOrbitChip,
  ConnectorOrbitHub,
  ConnectorOrbitHubLabel,
  // Knowledge Locker
  // Connectors
  ConnectorWall,
  ConnectorGroupBlock,
  ConnectorGroupLabel,
  ConnectorChipsRow,
  ConnectorChipItem,
  ConnectorFootnote,
  // RFP Visual
  RfpVisual,
  RfpSource,
  RfpSourceBox,
  RfpArrows,
  RfpTargets,
  RfpTarget,
  // Security
  SecurityGrid,
  SecurityCard,
  SecurityIcon,
  SecurityTitle,
  SecurityDesc,
  SecurityBadge,
  StatCard,
  StatValue,
  StatLabel,
  StatSublabel,
  // Logo Carousel
  LogoCarouselSection,
  LogoCarouselHeader,
  LogoCarouselWrapper,
  LogoCarouselTrackWrapper,
  LogoCarouselTrack,
  LogoCarouselItem,
  // Metrics
  MetricsBanner,
  MetricItem,
  MetricValue,
  MetricLabel,
  // How It Works Animation
  HowItWorksAnimationContainer,
  AnimationStage,
  TypingInputBox,
  TypedText,
  TypingCursor,
  ResearchPhaseContainer,
  ResearchHeader,
  ResearchDotsContainer,
  PulsingDot,
  ResearchLabel,
  ResearchLinksContainer,
  ResearchLinkCard,
  LinkFavicon,
  LinkContent,
  LinkTitle,
  LinkUrl,
  LinkCheckmark,
  SlidesContainer,
  SlidesHeader,
  SlidesGrid,
  SlideCard,
  SlideCardHeader,
  SlideCardBody,
  SlideCardLine,
  SlideCardChart,
  ChartBar,
  SlideCardBullets,
  BulletPoint,
  IterateContainer,
  IterateHeader,
  IterateSlidePreview,
  IterateHighlight,
  IterateChatBubble,
  IterateChatMessage,
  ChatAvatar,
  ChatText,
  IterateChangePill,
  ChatTypingDots,
  landingHeroStyles,
  // Blog
  BlogGrid,
  BlogCard,
  BlogCategory,
  BlogTitle,
  BlogExcerpt,
  BlogMeta,
  BlogReadMore,
  // Executive pullquote (P3)
  // Use case library (P0)
  UseCaseCategories,
  UseCaseCategory,
  UseCaseCategoryLabel,
  UseCaseGrid,
  UseCaseCard,
  UseCaseIcon,
  UseCaseBody,
  UseCaseName,
  UseCaseTime,
  UseCaseFootnote,
  UseCasesMarqueeWrapper,
  UseCasesMarqueeTrack,
  UseCaseMarqueeCard,
  UseCaseMarqueeCategory,
  // Case study cards (P1)
  CaseStudyCard,
  CaseStudyHead,
  CaseStudyTag,
  CaseStudyMetric,
  CaseStudyText,
  CaseStudyAuthor,
  CaseStudyAvatar,
  CaseStudyAuthorName,
  CaseStudyAuthorRole,
  TestimonialMarqueeWrapper,
  TestimonialMarqueeTrack,
  ImpactStatsRow,
  // Integrations strip (P2)
  IntegrationsSection,
  IntegrationsGroup,
  IntegrationsGroupLabel,
  IntegrationsRow,
  IntegrationChip,
  IntegrationsFootnote,
} from './HomePage.styles';

// ---------------------------------------------------------------------------
// Local hook: useSignIn
// ---------------------------------------------------------------------------
const useSignIn = () => {
  const { signIn } = useAuth();

  const [signInOpen, setSignInOpen] = useState(false);
  const [signInLoading, setSignInLoading] = useState(false);
  const [signInError, setSignInError] = useState(null);
  const [emailMode, setEmailMode] = useState('signin'); // signin | signup | reset
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  const handleOpenSignIn = useCallback(() => setSignInOpen(true), []);
  const handleCloseSignIn = useCallback(() => {
    setSignInOpen(false);
    setSignInError(null);
    setEmailMode('signin');
  }, []);

  const handleSignIn = useCallback(async () => {
    try {
      setSignInLoading(true);
      setSignInError(null);
      await signIn();
      handleCloseSignIn();
    } catch (error) {
      console.error('Sign in error:', error);
      let errorMessage = 'Failed to sign in. Please try again.';
      if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup was blocked by your browser. Please allow popups for this site.';
      } else if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in was cancelled.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      setSignInError(errorMessage);
    } finally {
      setSignInLoading(false);
    }
  }, [signIn, handleCloseSignIn]);

  // Microsoft / Azure AD sign-in. Restack's customer base is overwhelmingly
  // on Microsoft 365 (it's a PowerPoint plugin), so MS sign-in is a peer
  // option to Google on the landing page — not a hidden fallback.
  const handleMicrosoftSignIn = useCallback(async () => {
    try {
      setSignInLoading(true);
      setSignInError(null);
      const { signInWithMicrosoft } = await import('../../shared/services/firebase');
      await signInWithMicrosoft();
      handleCloseSignIn();
    } catch (error) {
      console.error('Microsoft sign-in error:', error);
      setSignInError(error.message || 'Microsoft sign-in failed. Please try again.');
    } finally {
      setSignInLoading(false);
    }
  }, [handleCloseSignIn]);

  const handleEmailSubmit = useCallback(async (e) => {
    e?.preventDefault();
    if (emailLoading) return;
    setSignInError(null);
    setEmailLoading(true);
    try {
      const { signInWithEmail, signUpWithEmail, resetPassword } = await import('../../shared/services/firebase');
      if (emailMode === 'reset') {
        await resetPassword(emailInput.trim());
        setEmailMode('signin');
        setSignInError('Reset link sent! Check your email.');
      } else if (emailMode === 'signup') {
        await signUpWithEmail(emailInput.trim(), passwordInput, nameInput.trim() || null);
        setEmailMode('signin');
        setSignInError('Account created! Check your email for a verification link, then sign in.');
      } else {
        await signInWithEmail(emailInput.trim(), passwordInput);
        handleCloseSignIn();
      }
    } catch (err) {
      setSignInError(err.message);
    } finally {
      setEmailLoading(false);
    }
  }, [emailMode, emailInput, passwordInput, nameInput, emailLoading, handleCloseSignIn]);

  return {
    signInOpen,
    signInLoading,
    signInError,
    setSignInError,
    handleOpenSignIn,
    handleCloseSignIn,
    handleSignIn,
    handleMicrosoftSignIn,
    emailMode, setEmailMode,
    emailInput, setEmailInput,
    passwordInput, setPasswordInput,
    nameInput, setNameInput,
    emailLoading,
    handleEmailSubmit,
  };
};



// ---------------------------------------------------------------------------
// Landing FAQ
// ---------------------------------------------------------------------------
// The eight questions a deal team actually raises before trialling, ordered by
// how early the objection lands: what is it, how is it different, does the
// research hold, is it safe, does it run on our Office, what does it cost, can
// the team share it, and what if it does not work out. Pulled by id from
// FAQ_REPOSITORY so the page and the support widget can never drift apart.
const LANDING_FAQ_IDS = [
  'different-from-chatgpt',
  'how-works',
  'research-quality',
  'security',
  'powerpoint-versions',
  'pricing',
  'collaboration',
  'refund',
];
const LANDING_FAQ = LANDING_FAQ_IDS
  .map((id) => FAQ_REPOSITORY.find((entry) => entry.id === id))
  .filter(Boolean);

// FAQPage structured data, so answer engines can quote these directly.
const LANDING_FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: LANDING_FAQ.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
};

// ---------------------------------------------------------------------------
// FAQ category grouping
// ---------------------------------------------------------------------------
// Buckets the 11 questions in FAQ_REPOSITORY into 3 thematic columns so the
// welcome screen reads as a quick "menu" rather than an 11-row vertical list.
// The grid layout (CSS) renders each bucket as a 2-column tile set, so a
// reader's eye scans label → 4 chips → next label, instead of 12 chips deep.
//
// We re-derive this each render from the repository to keep a single source
// of truth for question text/answers — only the *grouping* lives here.
const FAQ_CATEGORY_GROUPS = [
  {
    label: 'Start here',
    ids: ['what-is-restack', 'how-works', 'different-from-chatgpt', 'getting-started'],
  },
  {
    label: 'Quality & coverage',
    ids: ['research-quality', 'frameworks', 'powerpoint-versions', 'collaboration'],
  },
  {
    label: 'Plans & policies',
    ids: ['pricing', 'security', 'refund', 'contact'],
  },
];

// ---------------------------------------------------------------------------
// Local component: FAQChatWidget
// ---------------------------------------------------------------------------
const FAQChatWidget = ({ isDarkMode = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [askedIds, setAskedIds] = useState(() => new Set());
  const messagesEndRef = useRef(null);
  const lastMessageRef = useRef(null);
  const inputRef = useRef(null);

  // Smart scroll: when a new message arrives, anchor the LATEST message to
  // the top of the visible area instead of slamming to the absolute bottom.
  // Why: the previous behavior scrolled past the answer to the "Keep
  // exploring" follow-ups, so users couldn't see the reply they'd just
  // requested. Anchoring the latest message to the top keeps the answer
  // (or the user's question) in view; follow-up chips appear naturally
  // below and are reachable by a short scroll.
  useEffect(() => {
    if (messages.length === 0) return;
    const node = lastMessageRef.current;
    if (!node) return;
    // requestAnimationFrame so DOM has painted the new bubble before scroll
    requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [messages.length]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    setError(null);
  };

  const handleClose = () => {
    setIsOpen(false);
    setError(null);
  };

  const handleBackToHome = () => {
    setMessages([]);
    setInputValue('');
    setError(null);
    setAskedIds(new Set());
  };

  const handleSendMessage = useCallback((messageText = inputValue.trim()) => {
    if (!messageText || isLoading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setError(null);

    // If the free-text question maps to a known FAQ entry, record its id so
    // we can hide it from the follow-up list.
    const match = findFAQAnswer(messageText);
    if (match?.id) {
      setAskedIds((prev) => {
        const next = new Set(prev);
        next.add(match.id);
        return next;
      });
    }

    // Short "typing" beat so the canned answer doesn't feel instantaneous.
    setIsLoading(true);
    setTimeout(() => {
      const answer = match ? match.answer : FAQ_FALLBACK_ANSWER;
      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: answer,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setIsLoading(false);
    }, 380);
  }, [inputValue, isLoading]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleQuestionClick = useCallback((item) => {
    if (isLoading) return;
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: item.question,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setError(null);
    setAskedIds((prev) => {
      const next = new Set(prev);
      next.add(item.id);
      return next;
    });
    setIsLoading(true);
    setTimeout(() => {
      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: item.answer,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setIsLoading(false);
    }, 380);
  }, [isLoading]);

  const formatTime = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  return (
    <Box className={`faq-chat-widget ${isDarkMode ? 'dark' : 'light'}`}>
      {/* Chat Window */}
      <Grow in={isOpen} timeout={200}>
        <Box className="faq-chat-window">
          {/* Header — always shows the brand logo so the user knows what
              they're talking to. A back-to-home button appears on the LEFT
              of the logo (not in place of it) once the conversation has
              started, mirroring the pattern in the PowerPoint chat panel. */}
          <Box className="faq-chat-header">
            <Box className="faq-chat-header__left">
              {messages.length > 0 && (
                <IconButton
                  onClick={handleBackToHome}
                  size="small"
                  className="faq-chat-header__back"
                  aria-label="Back to home"
                  title="Back to topics"
                >
                  <ArrowBackIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
              <Avatar
                className="faq-chat-header__avatar"
                sx={{ width: 30, height: 30 }}
              >
                <brandConfig.icon sx={{ fontSize: 16 }} />
              </Avatar>
              <Typography className="faq-chat-header__title">
                Restack
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              size="small"
              className="faq-chat-header__close"
              aria-label="Close chat"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Messages Area */}
          <Box className="faq-chat-messages">
            {/* Welcome message if no messages */}
            {messages.length === 0 && (
              <Fade in timeout={400}>
                <Box className="faq-chat-welcome">
                  <Box className="faq-chat-welcome__icon">
                    <brandConfig.icon sx={{ fontSize: 24 }} />
                  </Box>
                  <Typography className="faq-chat-welcome__title">
                    How can we help?
                  </Typography>
                  <Typography className="faq-chat-welcome__text">
                    Pick a topic, or ask your own question.
                  </Typography>

                  {/* Categorized question menu. Each group renders as a
                      2-column grid of compact chips so the user scans
                      "topic → 4 questions" instead of slogging through an
                      11-row vertical list. */}
                  <Box className="faq-chat-categories">
                    {FAQ_CATEGORY_GROUPS.map((group) => {
                      const items = group.ids
                        .map((id) => FAQ_REPOSITORY.find((q) => q.id === id))
                        .filter(Boolean);
                      if (items.length === 0) return null;
                      return (
                        <Box key={group.label} className="faq-chat-category">
                          <Typography className="faq-chat-category__label">
                            {group.label}
                          </Typography>
                          <Box className="faq-chat-suggestions faq-chat-suggestions--grid">
                            {items.map((item) => (
                              <Chip
                                key={item.id}
                                label={item.question}
                                onClick={() => handleQuestionClick(item)}
                                className="faq-chat-suggestion-chip"
                                size="small"
                              />
                            ))}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Fade>
            )}

            {/* Message list — attach lastMessageRef to the latest message so
                the smart-scroll effect can anchor it to the top of view. */}
            {messages.map((message, idx) => (
              <Fade in key={message.id} timeout={300}>
                <Box
                  ref={idx === messages.length - 1 ? lastMessageRef : null}
                  className={`faq-chat-message ${message.role === 'user' ? 'faq-chat-message--user' : 'faq-chat-message--assistant'} ${message.isError ? 'faq-chat-message--error' : ''}`}
                >
                  {message.role === 'assistant' && (
                    <Avatar
                      className="faq-chat-message__avatar"
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
                      }}
                    >
                      <brandConfig.icon
                        sx={{
                          fontSize: 14,
                          color: isDarkMode ? '#fafafa' : '#18181b',
                        }}
                      />
                    </Avatar>
                  )}
                  <Box className="faq-chat-message__content">
                    <Typography className="faq-chat-message__text">
                      {message.content}
                    </Typography>
                    <Typography className="faq-chat-message__time">
                      {formatTime(message.timestamp)}
                    </Typography>
                  </Box>
                </Box>
              </Fade>
            ))}

            {/* Follow-up questions — show remaining repository entries after
                the latest assistant reply so the user can keep exploring. */}
            {!isLoading &&
              messages.length > 0 &&
              messages[messages.length - 1].role === 'assistant' &&
              (() => {
                const remaining = FAQ_REPOSITORY.filter(
                  (item) => !askedIds.has(item.id)
                );
                if (remaining.length === 0) return null;
                return (
                  <Fade in timeout={300}>
                    <Box className="faq-chat-followups">
                      <Typography className="faq-chat-followups__label">
                        Keep exploring
                      </Typography>
                      <Box className="faq-chat-suggestions faq-chat-suggestions--grid faq-chat-suggestions--followups">
                        {remaining.map((item) => (
                          <Chip
                            key={item.id}
                            label={item.question}
                            onClick={() => handleQuestionClick(item)}
                            className="faq-chat-suggestion-chip"
                            size="small"
                          />
                        ))}
                      </Box>
                    </Box>
                  </Fade>
                );
              })()}

            {/* Loading indicator */}
            {isLoading && (
              <Fade in timeout={200}>
                <Box className="faq-chat-message faq-chat-message--assistant faq-chat-message--loading">
                  <Avatar
                    className="faq-chat-message__avatar"
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
                    }}
                  >
                    <brandConfig.icon
                      sx={{
                        fontSize: 14,
                        color: isDarkMode ? '#fafafa' : '#18181b',
                      }}
                    />
                  </Avatar>
                  <Box className="faq-chat-typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </Box>
                </Box>
              </Fade>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box className="faq-chat-input-area">
            <TextField
              inputRef={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask a question..."
              fullWidth
              size="small"
              disabled={isLoading}
              autoComplete="off"
              className="faq-chat-input"
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => handleSendMessage()}
                    disabled={!inputValue.trim() || isLoading}
                    size="small"
                    className="faq-chat-send-button"
                    aria-label="Send message"
                  >
                    {isLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                ),
              }}
            />
          </Box>
        </Box>
      </Grow>

      {/* Floating Button — the "Any questions?" label that previously
          floated next to this FAB has been removed. The icon alone is
          self-evident as a chat trigger and the popup label was visual
          noise on the landing page. */}
      <Fade in={!isOpen} timeout={200}>
        <Box
          className="faq-chat-fab-container"
          sx={{ display: isOpen ? 'none' : 'flex' }}
        >
          <IconButton
            onClick={handleToggle}
            className="faq-chat-fab"
            aria-label="Open chat support"
          >
            <brandConfig.icon sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>
      </Fade>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// AnimatedNumber — counts up from 0 to target when scrolled into view.
// Preserves non-numeric parts (e.g. "%", "x", "+") in the source string.
// ---------------------------------------------------------------------------
const AnimatedNumber = React.forwardRef(function AnimatedNumber(
  { value, duration = 1600, component: Component = 'span', ...rest },
  forwardedRef
) {
  const localRef = useRef(null);
  const startedRef = useRef(false);
  const [display, setDisplay] = useState(() => {
    const m = String(value).match(/^(\D*)([0-9]+(?:\.[0-9]+)?)(.*)$/);
    return m ? `${m[1]}0${m[3]}` : String(value);
  });

  useEffect(() => {
    const el = localRef.current;
    if (!el) return undefined;
    const m = String(value).match(/^(\D*)([0-9]+(?:\.[0-9]+)?)(.*)$/);
    if (!m) {
      setDisplay(String(value));
      return undefined;
    }
    const [, prefix, numberStr, suffix] = m;
    const target = parseFloat(numberStr);
    const isInt = !numberStr.includes('.');
    const decimals = isInt ? 0 : numberStr.split('.')[1].length;

    startedRef.current = false;
    setDisplay(`${prefix}0${suffix}`);

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setDisplay(String(value));
      return undefined;
    }

    const run = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const start = performance.now();
      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = eased * target;
        const formatted = isInt ? Math.round(current).toString() : current.toFixed(decimals);
        setDisplay(`${prefix}${formatted}${suffix}`);
        if (progress < 1) requestAnimationFrame(step);
        else setDisplay(String(value));
      };
      requestAnimationFrame(step);
    };

    if (typeof IntersectionObserver === 'undefined') {
      run();
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) run();
        });
      },
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  const setRefs = (node) => {
    localRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  return (
    <Component ref={setRefs} {...rest}>
      {display}
    </Component>
  );
});

const HomePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { authLoading } = useUser();
  const signInData = useSignIn();


  // Announcement banner hides once the user scrolls past the top. The page
  // scrolls inside LandingContainer (not the window), so we listen in the
  // capture phase to catch scroll events from any scrolling element.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const readTop = (e) => {
      const t = e && e.target;
      if (t && t !== document && t !== window && typeof t.scrollTop === 'number') {
        return t.scrollTop;
      }
      return window.scrollY || document.documentElement.scrollTop || 0;
    };
    const onScroll = (e) => {
      const t = e && e.target;
      if (t && t.closest && t.closest('.faq-chat-messages')) return;
      setScrolled(readTop(e) > 16);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  // Notification state
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  // Blog lightbox state — synced with URL hash (`#blog/<slug>`)
  const [openBlogSlug, setOpenBlogSlug] = useState(null);

  // Read slug from hash on mount + when hash changes (back/forward buttons)
  useEffect(() => {
    const parseHash = () => {
      const hash = window.location.hash || '';
      const match = hash.match(/^#blog\/([a-z0-9-]+)$/i);
      const slug = match ? match[1] : null;
      if (slug && blogPosts[slug]) {
        setOpenBlogSlug(slug);
      } else {
        setOpenBlogSlug(null);
      }
    };
    parseHash();
    window.addEventListener('hashchange', parseHash);
    return () => window.removeEventListener('hashchange', parseHash);
  }, []);

  const handleOpenBlog = useCallback((slug) => (event) => {
    if (event) {
      // Allow cmd/ctrl/shift-click to open the full page in a new tab
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
    }
    if (!blogPosts[slug]) return;
    // pushState so back button closes the modal
    window.history.pushState(null, '', `#blog/${slug}`);
    setOpenBlogSlug(slug);
    capture?.('blog_article_opened', { slug, surface: 'modal' });
  }, []);

  const handleCloseBlog = useCallback(() => {
    setOpenBlogSlug(null);
    // Collapse the blog-slug hash back to the section anchor, without
    // growing history. Keeps the user's scroll near the Blog section.
    if ((window.location.hash || '').startsWith('#blog/')) {
      const { pathname, search } = window.location;
      window.history.replaceState(null, '', `${pathname}${search}#blog`);
    }
  }, []);

  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => setNotification({ ...notification, open: false });

  // Testimonials — consulting-led mix with one PE deal-team voice so
  // both buyer profiles see themselves in the story without either
  // audience feeling like an afterthought. Three consulting (Senior
  // Consultant, Boutique Strategy, Principal Strategy) + one PE
  // (Mid-Market VP) is the right balance: consulting is the primary
  // positioning, PE reads as a recognised second use case.
  const testimonials = [
    {
      text: "I was honestly skeptical, I've tried every AI slide tool and they all produce the same generic output. But I had a market overview due at 2am for the steering committee and figured why not. It pulled in the right industry data, formatted the bar charts correctly, and I barely had to touch it. It's not perfect for everything, but for that kind of grind-it-out slide work? Game changer.",
      initials: "SM",
      name: "Sarah M.",
      role: "Senior Consultant, Tier-1 Strategy"
    },
    {
      text: "I used to lose entire weekends to client decks, formatting the same tables and exhibits over and over. Restack cut my last deliverable from about 12 hours to 3, and it all happened inside PowerPoint. No exporting, no copy-pasting from some other app. I actually left the office before midnight on a Friday for once.",
      initials: "JR",
      name: "James R.",
      role: "Consultant, Boutique Strategy"
    },
    {
      text: "What impressed me is that it actually reads the slide master. I've rejected every AI tool my team has brought me because the output always needs reformatting, wrong fonts, wrong colors, layouts that break our templates. Restack's output came back on-brand, not something my team had to reformat. That's the bar, and it's the first tool to clear it.",
      initials: "PK",
      name: "Priya K.",
      role: "Principal, Strategy Consulting"
    },
    {
      text: "We rolled it out to the deal team, billed it to the project code, and saw ROI in the first week. Three associates, 40+ hours saved on a single CIM. That math is straightforward.",
      initials: "MT",
      name: "Michael T.",
      role: "VP, Mid-Market PE"
    }
  ];

  // Scroll reveal for the animated blocks on this page.
  const isVisible = useScrollReveal();

  // While loading auth, show nothing
  if (authLoading) return null;

  // Data definitions - agents array removed, integrated into flow

  // The shipped capability cards. The first four each carry a visual: the
  // graphics do the explaining. Any card marked `upcoming` renders a "Rolling
  // out next" badge, which keeps a roadmap claim honest; none currently is.
  const capabilities = [
    {
      icon: <VerifiedUserIcon />,
      title: 'Audit-ready citations',
      desc: 'Every figure traces to the exact page and paragraph across the deck, model, and memo. One click to the source.',
      tags: ['Every Claim Cited', 'Source Trails'],
      variant: 'purple',
      visual: 'citations',
    },
    {
      icon: <PolicyRounded />,
      title: 'Holds your firm\'s template',
      desc: 'It holds your slide master and house style: fonts, palette, margins, disclaimers. What lands is client-ready.',
      tags: ['Brand Enforcement', 'Native PPTX'],
      variant: 'cyan',
      visual: 'governance',
    },
    // The chart-grammar and QA-pass cards were removed: both capabilities
    // already carry their own animated rows in How-it-works directly above,
    // and repeating them here diluted the grid.
    {
      icon: <LockIcon />,
      title: 'The private knowledge locker',
      desc: 'Sealed per engagement. No cross-project leakage, never trains a model.',
      tags: ['Single-Tenant', 'Zero Training'],
      variant: 'green',
      visual: 'locker',
    },
    {
      icon: <HubRounded />,
      title: 'Your data stack, connected',
      desc: 'Works alongside your market terminals, document stores, and the open web, under your firm\'s credentials.',
      tags: ['BYOC Credentials'],
      variant: 'orange',
      visual: 'connectors',
    },
    // Model choice. Deliberately the last card and deliberately visual-free:
    // the workflow argument leads this page, and this is the supporting
    // structural point (every in-app rival is tied to one lab). `wide` makes
    // it span both bento columns on desktop, so five cards still close on a
    // full row instead of leaving an orphan half-card.
    {
      icon: <SwapHorizRounded />,
      title: 'Not locked to one lab',
      desc: 'Opus 5 runs by default. Switch to Sonnet, Haiku, GPT-5.6 or Gemini between turns of the same chat, so a slide can get a second opinion without leaving your file.',
      tags: ['Frontier by Default', 'Switch Between Turns'],
      variant: 'orange',
      wide: true,
    },
  ];

  // Logo carousel data — SVGs and transparent PNGs for dark mode compatibility.
  // Per-logo height removed: the .landing-hero__logo-marquee-img CSS rule now
  // applies a uniform max-height + max-width with object-fit: contain so every
  // brand renders inside a consistent visual box regardless of native aspect.
  const clientLogos = [
    { name: 'Deloitte', logo: '/logos/deloitte.png' },
    { name: 'Accenture', logo: '/logos/accenture.png' },
    { name: 'PwC', logo: '/logos/pwc.png' },
    { name: 'Kearney', logo: '/logos/kearney.svg' },
    { name: 'OliverWyman', logo: '/logos/oliver-wyman.svg' },
    { name: 'LEK', logo: '/logos/lek.svg' },
    { name: 'RolandBerger', logo: '/logos/roland-berger.svg' },
  ];

  return (
    <LandingContainer data-theme="light">
      {/* The skip link now ships once from SiteHeader, so every marketing
          page gets it instead of each page hand-rolling one. */}
      {/* Per-route SEO — matches index.html defaults so SPA navigation
          back to "/" restores the canonical title/description even after
          a blog post or sub-route has overridden them. */}
      <SEO
        canonical="/"
        title="Restack | The deliverable engine for Private Equity teams"
        description="AI for Private Equity teams: the deliverable engine that builds the committee-ready deck, model, and memo in your live Office files, every figure cited."
        schema={LANDING_FAQ_SCHEMA}
      />

      {/* Inject landing hero styles */}
      <style>{landingHeroStyles}</style>

      {/* HEADER */}
      {/* Announcement banner — sits above the header, links to the latest
          essay. Homepage only; the header is offset down by its height. */}
      <Box
        component={RouterLink}
        to="/blog/pe-software-led"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 30,
          zIndex: 1201,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.85,
          px: 2,
          backgroundColor: '#F1EBE3',
          color: 'rgba(46, 20, 10, 0.66)',
          textDecoration: 'none',
          borderBottom: '1px solid rgba(46, 20, 10, 0.05)',
          fontFamily: theme.typography.fontFamily,
          fontSize: { xs: '0.66rem', md: '0.72rem' },
          fontWeight: 400,
          letterSpacing: '-0.005em',
          // Slide the banner up and out of the way once the user scrolls.
          transform: scrolled ? 'translateY(-100%)' : 'translateY(0)',
          opacity: scrolled ? 0 : 1,
          pointerEvents: scrolled ? 'none' : 'auto',
          transition: 'transform 0.3s ease, opacity 0.3s ease',
        }}
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Half the PE industry calls itself software-led now. PE is going AI-led.
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            Half the PE industry calls itself software-led now.
          </Box>
        </Box>
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            color: '#B83B1D',
            fontWeight: 500,
            textDecoration: 'underline',
            textUnderlineOffset: '0.18em',
          }}
        >
          Learn more
        </Box>
      </Box>

      {/* Header login is a LINK to the console — no sign-in dialog on the
          landing page. (The legacy dialog below stays mounted but is no
          longer reachable from the header.) */}
      <SiteHeader offsetTop={scrolled ? 0 : 30} />

      {/* HERO SECTION with PowerPoint Mockup. Opens the <main> landmark
          (the skip link's target), so assistive tech can jump the header
          and announcement banner straight to the page's content. */}
      <Box component="main" id="main-content" className="landing-hero">
        <HeroDecoration />

        {/* Centered Content */}
        <Box className="landing-hero__content" sx={{ '&.landing-hero__content': { pt: { md: '7vh' } } }}>
          {/* Hero headline + subhead. "AI editor" is the rust serif accent;
              the rest stays black sans so emphasis lands on the category. */}
          <Box className="landing-hero__headline-wrapper">
            <Typography variant="h1" className="landing-hero__headline">
              The <span className="landing-hero__headline-accent">deliverable engine</span> for Private Equity teams
            </Typography>
          </Box>
          <Typography
            sx={{
              maxWidth: { xs: '92%', sm: 600, md: 720 },
              mx: 'auto',
              mt: { xs: 1.5, md: 2.5 },
              textAlign: 'center',
              fontFamily: theme.typography.fontFamily,
              fontSize: { xs: '0.9rem', md: '1.05rem' },
              fontWeight: 400,
              lineHeight: 1.55,
              letterSpacing: '-0.01em',
              color: alpha(theme.palette.text.primary, 0.55),
            }}
          >
            It builds the board-ready deck, model, and memo your team rebuilds every cycle, on your firm's template in your live Office files, with the chart grammar and citations a generic copilot skips.
          </Typography>


        </Box>

        {/* CTA row — placed ABOVE the mockup. Two pills: AppSource
            credential + 1-minute demo. On desktop, DOM order alone
            keeps it between content and mockup; the .landing-hero__cta-row
            CSS class handles the explicit order on tablet/mobile where
            other siblings have explicit orders. */}
        <Box
          className="landing-hero__cta-row"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.75,
            textAlign: 'center',
            px: 2,
            // Centered vertically between the subhead and the mockup. pt (the
            // gap above the buttons) is set in fixed px to exactly equal the
            // gap below them (pb + the mockup's fixed marginTop), so the
            // centering holds at every viewport height. md: 34 = pb 8 + 26.
            // xs: 28 = pb 4 + 24.
            pt: { xs: '56px', md: '34px' },
            pb: { xs: 5.5, md: 1 },
          }}
        >
          {/* Row 1: AppSource credential + Request a Demo (links to /demo). */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 0.75, sm: 1.25 },
              // One row at every size. The secondary (AppSource) stays
              // content-width; the primary CTA flex-grows to take the rest, so
              // its longer label gets the room it needs without overflowing.
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Box
              component="a"
              href="https://marketplace.microsoft.com/en-us/product/WA200010426"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Live on Microsoft Store, opens in new tab"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                flex: '0 0 auto',
                minWidth: 0,
                maxWidth: '100%',
                px: { xs: 1.5, sm: 2, md: 3 },
                height: 48,
                borderRadius: '999px',
                fontFamily: theme.typography.fontFamily,
                fontWeight: 500,
                fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
                letterSpacing: '-0.005em',
                lineHeight: 1.45,
                whiteSpace: 'nowrap',
                color: theme.palette.text.primary,
                // Secondary CTA: light warm-cream fill with a hairline
                // border, paired against the solid dark primary.
                backgroundColor: '#f4f1ed',
                border: `1px solid ${alpha(theme.palette.text.primary, 0.16)}`,
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease, border-color 0.2s ease',
                '&:hover': {
                  backgroundColor: '#ede8e1',
                  borderColor: alpha(theme.palette.text.primary, 0.28),
                },
              }}
            >
              <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true" style={{ flexShrink: 0 }}>
                <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
              </svg>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Live on Microsoft Store</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Microsoft Store</Box>
            </Box>
            <Box
              component={RouterLink}
              to="/demo"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.8,
                flex: { xs: '1 1 auto', sm: '0 0 auto' },
                minWidth: 0,
                maxWidth: '100%',
                px: { xs: 1.5, sm: 2.25, md: 3.25 },
                height: 48,
                borderRadius: '999px',
                fontFamily: theme.typography.fontFamily,
                fontWeight: 600,
                fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
                letterSpacing: '-0.005em',
                lineHeight: 1.45,
                whiteSpace: 'nowrap',
                // Primary CTA: solid dark fill (matches the header
                // "Request a Demo" brand color) with white text.
                color: '#ffffff',
                backgroundColor: theme.palette.mode === 'dark' ? '#ffffff' : '#2E140A',
                ...(theme.palette.mode === 'dark' && { color: '#1a1a1a' }),
                border: 'none',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#f0f0f0' : '#1A0A04',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                },
              }}
            >
              See it on your own deck
            </Box>
          </Box>
        </Box>

        {/* The hero animation lives in OfficeHeroStage so /ai-transformation
            can render the same stage. */}
        <OfficeHeroStage />

        {/* Trust signal strip — sits directly below the hero mockup.
            DESKTOP: centered flex row with dot dividers (matches /demo).
            MOBILE: horizontal infinite-scroll marquee. The 2-col grid
            we had before looked left-aligned and visually inert; the
            marquee gives the row momentum and naturally centers the
            scanning eye. Soft edge mask fades items in/out at the
            viewport edges.

            order: 4 is required on tablet/mobile because the parent
            `.landing-hero` is `display: flex; flex-direction: column`
            and assigns explicit order values to its children at
            ≤1024px (mockup-wrapper = 3, marquee = 4, cards-scroll = 5).
            Without this, the strip defaults to order 0 and jumps to
            the very top of the hero. On desktop no other sibling has
            an explicit order, so this is a no-op visually. */}
        {/* Hero security strip wrapped out — security now has its own
            landing section + the /security page, so this line was
            redundant and crowded the top of the hero. */}
        {false && (
        <Box
          sx={{
            order: 4,
            mt: { xs: 2, md: 2.5 },
            mb: { xs: 1.5, md: 2 },
            px: { md: 4 },
            width: '100%',
            color: alpha(theme.palette.text.primary, 0.6),
            overflow: 'hidden',
            maskImage: {
              xs: 'linear-gradient(to right, transparent 0%, #000 6%, #000 94%, transparent 100%)',
              md: 'none',
            },
            WebkitMaskImage: {
              xs: 'linear-gradient(to right, transparent 0%, #000 6%, #000 94%, transparent 100%)',
              md: 'none',
            },
          }}
        >
          {/* MOBILE — horizontal infinite marquee. Items rendered twice
              so the translateX from 0 to -50% loops seamlessly. The
              dot divider after every item (including the last) makes
              the wrap-around continuous. */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              width: 'max-content',
              alignItems: 'center',
              animation: 'restackHeroSecurityMarquee 28s linear infinite',
              '@keyframes restackHeroSecurityMarquee': {
                '0%': { transform: 'translateX(0)' },
                '100%': { transform: 'translateX(-50%)' },
              },
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
              },
            }}
          >
            {[
              ...[
                { Icon: ShieldIcon, label: 'Firm-level isolation' },
                { Icon: AutoAwesomeIcon, label: 'Frontier AI · Zero data retention'},
                { Icon: PublicRounded, label: 'US or EU hosting' },
                { Icon: TimerRounded, label: '24-hour deletion' },
                { Icon: HistoryRounded, label: 'Audit trail' },
              ],
              ...[
                { Icon: ShieldIcon, label: 'Firm-level isolation' },
                { Icon: AutoAwesomeIcon, label: 'Frontier AI · Zero data retention'},
                { Icon: PublicRounded, label: 'US or EU hosting' },
                { Icon: TimerRounded, label: '24-hour deletion' },
                { Icon: HistoryRounded, label: 'Audit trail' },
              ],
            ].map(({ Icon, label }, idx) => (
              <Box
                key={`hero-sec-${idx}`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.55,
                    px: 2,
                  }}
                >
                  <Icon sx={{ fontSize: 13, flexShrink: 0 }} />
                  <Box
                    component="span"
                    sx={{
                      fontFamily: theme.typography.fontFamily,
                      fontSize: '0.72rem',
                      fontWeight: 500,
                      letterSpacing: '-0.005em',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.25,
                    }}
                  >
                    {label}
                  </Box>
                </Box>
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 2,
                    height: 2,
                    borderRadius: '50%',
                    background: alpha(theme.palette.text.primary, 0.28),
                    flexShrink: 0,
                  }}
                />
              </Box>
            ))}
          </Box>

          {/* DESKTOP — centered flex row with dot dividers, unchanged. */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              rowGap: 0.5,
            }}
          >
            {[
              { Icon: ShieldIcon, label: 'Firm-level isolation' },
              { Icon: AutoAwesomeIcon, label: 'Frontier AI · Zero data retention'},
              { Icon: PublicRounded, label: 'US or EU hosting' },
              { Icon: TimerRounded, label: '24-hour deletion' },
              { Icon: HistoryRounded, label: 'Audit trail' },
            ].map(({ Icon, label }, index, arr) => (
              <React.Fragment key={label}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.55,
                    px: 1.5,
                    minWidth: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 13, flexShrink: 0 }} />
                  <Box
                    component="span"
                    sx={{
                      fontFamily: theme.typography.fontFamily,
                      fontSize: '0.74rem',
                      fontWeight: 500,
                      letterSpacing: '-0.005em',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.25,
                    }}
                  >
                    {label}
                  </Box>
                </Box>
                {index < arr.length - 1 && (
                  <Box
                    aria-hidden="true"
                    sx={{
                      display: 'inline-block',
                      width: 2,
                      height: 2,
                      borderRadius: '50%',
                      background: alpha(theme.palette.text.primary, 0.28),
                      flexShrink: 0,
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>
        )}

        {/* Scrolling Logo Marquee - disabled. Replaced by the Design
            Partner band below; flip `false` to restore. */}
        {false && (
        <Box className="landing-hero__logo-marquee">
          <Typography className="landing-hero__logo-marquee-label">
            Privately testing with select case teams from leading strategy and advisory firms
          </Typography>
          <Box className="landing-hero__logo-marquee-track-wrapper">
            <Box className="landing-hero__logo-marquee-track">
              {clientLogos.map((company) => (
                <Box
                  key={company.name}
                  className="landing-hero__logo-marquee-slot"
                >
                  <Box
                    component="img"
                    src={company.logo}
                    alt={company.name}
                    className="landing-hero__logo-marquee-img"
                  />
                </Box>
              ))}
              {clientLogos.map((company) => (
                <Box
                  key={`${company.name}-dup`}
                  className="landing-hero__logo-marquee-slot"
                >
                  <Box
                    component="img"
                    src={company.logo}
                    alt=""
                    aria-hidden="true"
                    className="landing-hero__logo-marquee-img"
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        )}
      </Box>

      {/* TESTIMONIAL SLOT: refill only with a real, attributable quote
          (Unleashed podcast episode airs Jul 6). Never a composite or
          illustrative quote. The interim trust-marker strip was removed:
          the hero CTA already carries the Microsoft Store credential. */}


      {/* HOW IT WORKS - Merged with Built-in Intelligence */}
      {/* HOW IT WORKS — the four proof artefacts (same component as
          /product). Replaced the demo video, which is delivered via
          the /demo flow and on request. */}
      <CapabilityShowcase isVisible={isVisible} loop />

      {/* LOGO CAROUSEL - Social Proof */}
      <LogoCarouselSection>
        <Container>
          <AnimateOnScroll
            isVisible={isVisible('logo-carousel')}
            data-animate-id="logo-carousel"
          >
            <LogoCarouselHeader>
              Privately testing with select case teams from leading strategy and advisory firms
            </LogoCarouselHeader>
            {/* Logo wall - disabled; flip `false` to restore. */}
            {false && (
            <LogoCarouselWrapper>
              <LogoCarouselTrackWrapper>
                <LogoCarouselTrack>
                  {clientLogos.map((logo) => (
                    <LogoCarouselItem key={logo.name}>
                      <img
                        src={logo.logo}
                        alt={logo.name}
                        style={{ height: logo.height }}
                      />
                    </LogoCarouselItem>
                  ))}
                  {clientLogos.map((logo) => (
                    <LogoCarouselItem key={`${logo.name}-dup`} aria-hidden="true">
                      <img
                        src={logo.logo}
                        alt=""
                        style={{ height: logo.height }}
                      />
                    </LogoCarouselItem>
                  ))}
                </LogoCarouselTrack>
              </LogoCarouselTrackWrapper>
            </LogoCarouselWrapper>
            )}
            <MetricsBanner>
              {/* Grounded arithmetic, not a pilot statistic. The old
                  "40% faster production, pilot teams, early data" was an
                  unmeasured figure of exactly the kind the honesty gates
                  already dropped elsewhere, and consulting clients now vet
                  this site. This is the time to a reviewed first draft in the
                  live file, against building the same thing from scratch. */}
              <MetricItem>
                <MetricValue as="div">
                  <AnimatedNumber value="~1.5 hr" />
                </MetricValue>
                <MetricLabel>Analyst finish on a 50-slide deck, vs about a week from scratch</MetricLabel>
              </MetricItem>
              <MetricItem>
                <MetricValue as="div">
                  <AnimatedNumber value="Never" />
                </MetricValue>
                <MetricLabel>Trained on your data</MetricLabel>
              </MetricItem>
              <MetricItem>
                <MetricValue as="div">
                  <AnimatedNumber value="100" />
                </MetricValue>
                <MetricLabel>Sources scanned per slide</MetricLabel>
              </MetricItem>
            </MetricsBanner>
          </AnimateOnScroll>
        </Container>
      </LogoCarouselSection>

      <Section id="capabilities">
        <Container>
          <AnimateOnScroll
            isVisible={isVisible('cap-header')}
            data-animate-id="cap-header"
          >
            <CenteredHeader>
              <SectionEyebrow>Core capabilities</SectionEyebrow>
              <SectionTitle component="h2">What <em>committee-grade</em> requires.</SectionTitle>
            </CenteredHeader>
          </AnimateOnScroll>

          <CapabilitiesBento>
            {capabilities.map((cap, i) => (
              <AnimateOnScroll
                key={i}
                isVisible={isVisible(`bento-${i}`)}
                delay={0.1 * ((i % 2) + 1)}
                data-animate-id={`bento-${i}`}
                sx={cap.wide ? { gridColumn: { xs: 'span 1', md: 'span 2' } } : undefined}
              >
                <BentoCard>
                  <BentoIcon variant={cap.variant}>{cap.icon}</BentoIcon>
                  <BentoTitleRow>
                    <BentoTitle>{cap.title}</BentoTitle>
                    {cap.upcoming && (
                      <BentoUpcomingBadge>
                        <NotificationsRounded />
                        Rolling out next
                      </BentoUpcomingBadge>
                    )}
                  </BentoTitleRow>
                  {/* The wide card spans both columns, so its line length is
                      capped to keep the same reading measure as the half-width
                      cards above it. */}
                  <BentoDesc sx={cap.wide ? { maxWidth: 760 } : undefined}>{cap.desc}</BentoDesc>

                  {cap.tags && (
                    <BentoTags>
                      {cap.tags.map((tag, j) => (
                        <BentoTag key={j}>{tag}</BentoTag>
                      ))}
                    </BentoTags>
                  )}

                  {/* ─── CITATIONS: Claim → Source trace ─────────────────── */}
                  {cap.visual === 'citations' && (
                    <CapVisualFrame>
                      <CitationTrace>
                        <CitationQuote>
                          &nbsp;&nbsp;Nordic fintech TAM grew 23% YoY to &euro;4.2B in 2026
                          <CitationChip>1</CitationChip>
                          <CitationChip>2</CitationChip>
                        </CitationQuote>
                        <CitationConnector />
                        <CitationSource>
                          <CitationSourceIcon />
                          <CitationSourceBody>
                            <CitationSourceTitle>ECB Fintech Report Q3 2026</CitationSourceTitle>
                            <CitationSourceMeta>Page 14 &middot; Paragraph 3</CitationSourceMeta>
                            <CitationSourceHighlight />
                          </CitationSourceBody>
                        </CitationSource>
                      </CitationTrace>
                    </CapVisualFrame>
                  )}

                  {/* ─── GOVERNANCE: Before/After brand compliance ─────────
                      Hex codes removed — readers don't need to parse RGB to
                      get the point. The swatch blobs do the work. */}
                  {cap.visual === 'governance' && (
                    <CapVisualFrame>
                      <GovCompare>
                        <GovPanel variant="bad">
                          <GovPanelLabel variant="bad">Generic AI</GovPanelLabel>
                          <GovHeadline variant="bad">Market Entry Strategy</GovHeadline>
                          <GovSwatchRow>
                            <GovSwatch color="#3B82F6" />
                            <GovSwatch color="#93C5FD" />
                            <GovSwatch color="#BFDBFE" />
                          </GovSwatchRow>
                        </GovPanel>
                        <GovArrow className="gov-arrow">&rarr;</GovArrow>
                        <GovPanel variant="good">
                          <GovPanelLabel variant="good">Restack</GovPanelLabel>
                          <GovHeadline variant="good">Market Entry Strategy</GovHeadline>
                          <GovSwatchRow>
                            <GovSwatch color="#B83B1D" />
                            <GovSwatch color="#D4694E" />
                            <GovSwatch color="#E8B8A4" />
                          </GovSwatchRow>
                        </GovPanel>
                      </GovCompare>
                      <GovChecklist>
                        <GovCheck>Typography</GovCheck>
                        <GovCheck>Palette</GovCheck>
                        <GovCheck>Margins</GovCheck>
                        <GovCheck>Disclaimers</GovCheck>
                      </GovChecklist>
                    </CapVisualFrame>
                  )}

                  {/* ─── KNOWLEDGE LOCKER: Isolated project vaults ─────────
                      Roadmap card ("Rolling out next" badge); the vault
                      iconography carries the isolation story. */}
                  {cap.visual === 'locker' && (
                    <CapVisualFrame>
                      <VaultStack>
                        <VaultCard index={0}>
                          <VaultLock><LockIcon /></VaultLock>
                          <VaultBody>
                            <VaultName>Project Atlas</VaultName>
                            <VaultMeta>Sealed &middot; Client A</VaultMeta>
                          </VaultBody>
                        </VaultCard>
                        <VaultIsolationLine />
                        <VaultCard index={1}>
                          <VaultLock><LockIcon /></VaultLock>
                          <VaultBody>
                            <VaultName>Project Nova</VaultName>
                            <VaultMeta>Sealed &middot; Client B</VaultMeta>
                          </VaultBody>
                        </VaultCard>
                        <VaultIsolationLine />
                        <VaultCard index={2}>
                          <VaultLock><LockIcon /></VaultLock>
                          <VaultBody>
                            <VaultName>Project Vega</VaultName>
                            <VaultMeta>Sealed &middot; Client C</VaultMeta>
                          </VaultBody>
                        </VaultCard>
                      </VaultStack>
                      <VaultBadges>
                        <VaultBadge>Single-Tenant</VaultBadge>
                        <VaultBadge>Zero Training</VaultBadge>
                        <VaultBadge>Purge on Close</VaultBadge>
                      </VaultBadges>
                    </CapVisualFrame>
                  )}

                  {/* ─── CONNECTORS: Hub-and-arc vendor orbit ──────────────
                      Roadmap card ("Rolling out next" badge); a central
                      Restack node with a ring of representative connectors. */}
                  {cap.visual === 'connectors' && (
                    <CapVisualFrame>
                      <ConnectorOrbit>
                        {[
                          { name: 'Bloomberg',  icon: <QueryStatsRounded />,    angle: -135 }, // lower-left
                          { name: 'Capital IQ', icon: <AssessmentRounded />,    angle: -90  }, // left
                          { name: 'PitchBook',  icon: <TrendingUpRounded />,    angle: -45  }, // upper-left
                          { name: 'SharePoint', icon: <FolderSharedRounded />,  angle: 0    }, // top
                          { name: 'Box',        icon: <StorageRounded />,       angle: 45   }, // upper-right
                          { name: 'OneDrive',   icon: <CloudQueueRounded />,    angle: 90   }, // right
                          { name: 'SEC EDGAR',  icon: <PolicyRounded />,        angle: 135  }, // lower-right
                        ].map(({ name, icon, angle }) => (
                          <ConnectorOrbitChip key={name} angle={angle}>
                            {icon}
                            {name}
                          </ConnectorOrbitChip>
                        ))}
                        <ConnectorOrbitHub>
                          <HubRounded />
                        </ConnectorOrbitHub>
                        <ConnectorOrbitHubLabel>Restack</ConnectorOrbitHubLabel>
                      </ConnectorOrbit>
                      <ConnectorFootnote>
                        Uses your firm&rsquo;s credentials &middot; more on request
                      </ConnectorFootnote>
                    </CapVisualFrame>
                  )}

                  {cap.quote && (
                    <BentoQuote>{cap.quote}</BentoQuote>
                  )}

                  {cap.exports && (
                    <BentoExport>
                      {cap.exports.map((exp, j) => (
                        <ExportBtn key={j} index={j}>{exp}</ExportBtn>
                      ))}
                    </BentoExport>
                  )}

                  {cap.rfp && (
                    <RfpVisual>
                      <RfpSource>
                        <RfpSourceBox>
                          <FileCopyIcon />
                        </RfpSourceBox>
                      </RfpSource>
                      <RfpArrows>
                        <span>&rarr;</span>
                        <span>&rarr;</span>
                        <span>&rarr;</span>
                      </RfpArrows>
                      <RfpTargets>
                        <RfpTarget index={0}><PeopleRounded /> Client A</RfpTarget>
                        <RfpTarget index={1}><PeopleRounded /> Client B</RfpTarget>
                        <RfpTarget index={2}><PeopleRounded /> Client C</RfpTarget>
                      </RfpTargets>
                    </RfpVisual>
                  )}
                </BentoCard>
              </AnimateOnScroll>
            ))}
          </CapabilitiesBento>

        </Container>
      </Section>

      {/* USE CASE LIBRARY — concrete outputs deal teams ship (P0) */}
      <Section id="use-cases" withGrid>
        <Container>
          {/* Named workflows lead so buyers recognize their deliverable before
              reading the reasons teams choose Restack. */}
          <Typography
            component="h2"
            sx={{
              textAlign: 'center',
              mb: 1.25,
              fontFamily: theme.typography.fontFamily,
              fontSize: { xs: '1.25rem', md: '1.6rem' },
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: theme.palette.text.primary,
            }}
          >
            The work your team runs every cycle
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              mb: { xs: 3, md: 4 },
              mx: 'auto',
              maxWidth: 640,
              fontFamily: theme.typography.fontFamily,
              fontSize: { xs: '0.95rem', md: '1.05rem' },
              fontWeight: 500,
              lineHeight: 1.55,
              color: theme.palette.text.secondary,
            }}
          >
            The IC deck, the board pack, the SteerCo report: the deliverables you
            rebuild by hand today. Restack runs them from your model, on your
            template, the same way every cycle.
          </Typography>

          <AnimateOnScroll
            isVisible={isVisible('use-cases-grid')}
            delay={0.1}
            data-animate-id="use-cases-grid"
          >
            {(() => {
              const useCasesAll = [
                { icon: <TrendingUpRounded />, name: 'Quarterly board pack', time: '20 min', cat: 'Recurring' },
                { icon: <GavelRounded />, name: 'IC memo & committee deck', time: '20 min', cat: 'Deal' },
                { icon: <ShowChartRounded />, name: '100-day plan', time: '20 min', cat: 'Value creation' },
                { icon: <SlideshowRounded />, name: 'SteerCo deck & final report', time: '30 min', cat: 'Client' },
                { icon: <PieChartRounded />, name: 'Market sizing (TAM/SAM/SOM)', time: '15 min', cat: 'Research' },
                { icon: <ListAltRounded />, name: 'Operating & LBO model', time: '25 min', cat: 'Model' },
                { icon: <DescriptionRounded />, name: 'Due diligence deck', time: '25 min', cat: 'Deal' },
                { icon: <CompareArrowsRounded />, name: 'Screening memo', time: '12 min', cat: 'Sourcing' },
                { icon: <PeopleRounded />, name: 'Board pre-read', time: '20 min', cat: 'Client' },
                { icon: <ArticleRounded />, name: 'Client status report', time: '10 min', cat: 'Recurring' },
              ];
              return (
                <UseCasesMarqueeWrapper>
                  <UseCasesMarqueeTrack className="use-cases-marquee-track">
                    {useCasesAll.map((uc, i) => (
                      <UseCaseMarqueeCard key={`${uc.name}-${i}-a`}>
                        <UseCaseIcon>{uc.icon}</UseCaseIcon>
                        <UseCaseBody>
                          <UseCaseMarqueeCategory>{uc.cat}</UseCaseMarqueeCategory>
                          <UseCaseName>{uc.name}</UseCaseName>
                        </UseCaseBody>
                      </UseCaseMarqueeCard>
                    ))}
                    {useCasesAll.map((uc, i) => (
                      <UseCaseMarqueeCard key={`${uc.name}-${i}-b`} aria-hidden="true">
                        <UseCaseIcon>{uc.icon}</UseCaseIcon>
                        <UseCaseBody>
                          <UseCaseMarqueeCategory>{uc.cat}</UseCaseMarqueeCategory>
                          <UseCaseName>{uc.name}</UseCaseName>
                        </UseCaseBody>
                      </UseCaseMarqueeCard>
                    ))}
                  </UseCasesMarqueeTrack>
                </UseCasesMarqueeWrapper>
              );
            })()}

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 3, md: 4 } }}>
              <Button
                component={RouterLink}
                to="/solutions"
                endIcon={<ArrowForwardRounded sx={{ fontSize: '1.05rem' }} />}
                sx={{
                  textTransform: 'none',
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  borderRadius: '999px',
                  px: 2.75,
                  py: 1,
                  color: theme.palette.text.primary,
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.18)}`,
                  '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.04) },
                }}
              >
                See all use cases
              </Button>
            </Box>
          </AnimateOnScroll>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '0.82fr 1.18fr' },
              gap: { xs: 3.5, md: 10 },
              alignItems: 'start',
              mt: { xs: 7, md: 10 },
            }}
          >
            {/* Left - section title (sticky on desktop) */}
            <AnimateOnScroll
              isVisible={isVisible('positioning-header')}
              data-animate-id="positioning-header"
            >
              <Box sx={{ position: { md: 'sticky' }, top: { md: '120px' } }}>
                <SectionEyebrow>Why Restack</SectionEyebrow>
                <SectionTitle
                  component="h2"
                  sx={{
                    textAlign: 'left',
                    fontSize: { xs: '1.4rem', md: '1.9rem' },
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  Why Private Equity teams choose <em>Restack</em>
                </SectionTitle>
                <SectionSubtitle sx={{ mx: 0, textAlign: 'left', fontSize: { xs: '0.9rem', md: '0.95rem' } }}>
                  From analysis to a committee-ready deliverable, built to a bar generic AI tools don't reach.
                </SectionSubtitle>
              </Box>
            </AnimateOnScroll>

            {/* Right - differentiator blocks */}
            <AnimateOnScroll
              isVisible={isVisible('positioning-grid')}
              delay={0.1}
              data-animate-id="positioning-grid"
            >
              <Box>
                {[
                  {
                    Icon: AppsRounded,
                    title: 'One workflow from model to memo',
                    body: 'Excel model, PowerPoint deck, and Word memo as one connected agent on your firm\'s templates, so the quarterly rebuild is one instruction: new data, same format.',
                  },
                  {
                    Icon: GroupsRounded,
                    title: 'Speaks Private Equity',
                    body: 'Action titles, the Pyramid Principle, IC-memo structure, your firm\'s conventions. Built for the work, not a generic copilot pointed at it.',
                  },
                  {
                    Icon: ShieldRounded,
                    title: 'Defensible in committee',
                    body: 'Every figure traces to its source: a cell, a chart, a paragraph. When the IC asks where a number came from, show them in one click.',
                  },
                  {
                    Icon: SlideshowRounded,
                    title: 'Builds the charts advisory work runs on',
                    body: 'Waterfalls, Mekkos, bridges, Gantts: the chart grammar PE decks run on, rendered natively in your slide, editable like any chart.',
                  },
                  {
                    Icon: FactCheckRounded,
                    title: 'Client-ready, not a first draft',
                    body: 'It lands structured, sourced, and on your template, so you refine the last mile instead of rebuilding from a rough cut.',
                  },
                  {
                    Icon: AutorenewRounded,
                    title: 'Iterates with you in the live file',
                    body: 'Refine it slide by slide in your live deck, not a one-shot draft.',
                  },
                ].map((item, i, arr) => (
                  <Box
                    key={item.title}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: { xs: 2, md: 2.5 },
                      pt: i === 0 ? 0 : { xs: 2.5, md: 4 },
                      pb: i === arr.length - 1 ? 0 : { xs: 2.5, md: 4 },
                      borderBottom:
                        i === arr.length - 1
                          ? 'none'
                          : `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                    }}
                  >
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: { xs: 40, md: 44 },
                        height: { xs: 40, md: 44 },
                        borderRadius: '12px',
                        backgroundColor: alpha('#B83B1D', 0.1),
                        color: '#B83B1D',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <item.Icon sx={{ fontSize: { xs: 20, md: 22 } }} />
                    </Box>
                    <Box>
                      <Typography
                        component="h3"
                        sx={{
                          fontFamily: theme.typography.fontFamily,
                          fontWeight: 600,
                          fontSize: { xs: '1rem', md: '1.125rem' },
                          color: theme.palette.text.primary,
                          mb: 1,
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: theme.typography.fontFamily,
                          fontSize: { xs: '0.8125rem', md: '0.875rem' },
                          lineHeight: { xs: 1.55, md: 1.6 },
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {item.body}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </AnimateOnScroll>
          </Box>

        </Container>
      </Section>

      {/* RESULTS */}
      <Section id="results" withGrid>
        <Container>
          <AnimateOnScroll
            isVisible={isVisible('results-header')}
            data-animate-id="results-header"
          >
            <CenteredHeader>
              <SectionEyebrow>Impact</SectionEyebrow>
              <SectionTitle component="h2">Deeper <em>analysis.</em> Faster cycles.</SectionTitle>
            </CenteredHeader>
          </AnimateOnScroll>

          {/* Top stats row — 3 stats. Kept qualitative/defensible: 3x and
              50+ are early-pilot anchors; the third is the experiential
              "hours to minutes" claim (no unmeasured percentage). */}
          <ImpactStatsRow>
            <AnimateOnScroll
              isVisible={isVisible('stat-0')}
              delay={0.05}
              data-animate-id="stat-0"
            >
              <StatCard>
                <StatValue as="div">
                  <AnimatedNumber value="Up to 100" />
                </StatValue>
                <StatLabel>Sources scanned per deliverable</StatLabel>
                <StatSublabel>Reports, filings, news</StatSublabel>
              </StatCard>
            </AnimateOnScroll>

            <AnimateOnScroll
              isVisible={isVisible('stat-1')}
              delay={0.1}
              data-animate-id="stat-1"
            >
              <StatCard>
                <StatValue as="div">
                  <AnimatedNumber value="Every figure" />
                </StatValue>
                <StatLabel>Cited to its source</StatLabel>
                <StatSublabel>One click to the source</StatSublabel>
              </StatCard>
            </AnimateOnScroll>

            <AnimateOnScroll
              isVisible={isVisible('stat-2')}
              delay={0.15}
              data-animate-id="stat-2"
            >
              <StatCard>
                <StatValue as="div">
                  <AnimatedNumber value="Minutes" />
                </StatValue>
                <StatLabel>To a sourced first draft</StatLabel>
                <StatSublabel>Where formatting took an afternoon</StatSublabel>
              </StatCard>
            </AnimateOnScroll>
          </ImpactStatsRow>

        </Container>

        {/* Testimonial marquee — slow horizontal "film roll" of case studies.
            Lives OUTSIDE Container so its width is naturally 100vw — the
            previous attempt to bleed via negative margins inside Container
            only worked on the left because `width: 100%` locked the right
            edge to Container's padding. Now the row truly spans edge-to-edge
            on both sides. The card list is rendered twice so the -50%
            translate loop is seamless. Edge fade and hover-pause live on
            the wrapper.

            DISABLED - testimonial strip hidden for now; flip `false`
            back to `true` to restore it. */}
        {false && (() => {
          const stories = [
            { tag: 'Market Overview', metric: '10 hrs saved', ...testimonials[0] },
            { tag: 'Client Deck', metric: '9 hrs saved', ...testimonials[1] },
            { tag: 'Brand Compliance', metric: 'On your firm\'s template', ...testimonials[2] },
            { tag: 'Deal Team ROI', metric: '40+ hrs / CIM', ...testimonials[3] },
          ];
          const loop = [...stories, ...stories];
          return (
            <TestimonialMarqueeWrapper>
              <TestimonialMarqueeTrack className="marquee-track">
                {loop.map((story, idx) => (
                  <CaseStudyCard
                    // Mark the duplicated half aria-hidden so screen readers
                    // don't read the same testimonial twice.
                    key={`${story.initials}-${idx}`}
                    aria-hidden={idx >= stories.length ? 'true' : undefined}
                  >
                    <CaseStudyHead>
                      <CaseStudyTag>{story.tag}</CaseStudyTag>
                      <CaseStudyMetric>
                        <TrendingUpRounded /> {story.metric}
                      </CaseStudyMetric>
                    </CaseStudyHead>
                    <CaseStudyText>&ldquo;{story.text}&rdquo;</CaseStudyText>
                    <CaseStudyAuthor>
                      <Box>
                        <CaseStudyAuthorName>{story.name}</CaseStudyAuthorName>
                        <CaseStudyAuthorRole>{story.role}</CaseStudyAuthorRole>
                      </Box>
                    </CaseStudyAuthor>
                  </CaseStudyCard>
                ))}
              </TestimonialMarqueeTrack>
            </TestimonialMarqueeWrapper>
          );
        })()}
      </Section>

      {/* SECURITY */}
      <Section id="security">
        <Container>
          {/* Two-panel layout: left = headline + a tight bullet list +
              link into the full overview; right = a magnified 2x2 grid of
              the compliance frameworks buyers screen on. Replaces the old
              5-card block so the section reads cleaner; the depth lives on
              /security. Status labels stay honest and match /security
              (SOC 2 + ISO are "Roadmap"; GDPR + CCPA are "Aligned"), so
              the homepage never contradicts the overview page. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '0.92fr 1.08fr' },
              gap: { xs: 5, md: 8 },
              alignItems: 'center',
            }}
          >
            {/* LEFT */}
            <AnimateOnScroll
              isVisible={isVisible('security-header')}
              data-animate-id="security-header"
            >
              <Box sx={{ textAlign: 'left' }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 1.5,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `1px solid ${alpha(theme.palette.text.primary, 0.14)}`,
                      backgroundColor: alpha(theme.palette.text.primary, 0.03),
                      color: '#B83B1D',
                    }}
                  >
                    <LockIcon sx={{ fontSize: 16 }} />
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontFamily: theme.typography.fontFamily,
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      color: '#B83B1D',
                    }}
                  >
                    Security
                  </Box>
                </Box>

                <SectionTitle
                  component="h2"
                  sx={{ textAlign: 'left', mb: { xs: 3, md: 3.5 } }}
                >
                  Security your IT team can <em>sign off on.</em>
                </SectionTitle>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 1.75,
                    mb: { xs: 3.5, md: 4 },
                  }}
                >
                  {[
                    { Icon: LockIcon, label: 'No training on your data' },
                    { Icon: CloudQueueRounded, label: 'Firm-level data isolation' },
                    { Icon: ShieldIcon, label: 'Encrypted in transit and at rest' },
                    { Icon: MemoryRounded, label: 'Sandboxed code execution' },
                  ].map(({ Icon, label }) => (
                    <Box
                      key={label}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <Icon
                        sx={{
                          fontSize: 20,
                          color: alpha(theme.palette.text.primary, 0.55),
                          flexShrink: 0,
                        }}
                      />
                      <Box
                        component="span"
                        sx={{
                          fontFamily: theme.typography.fontFamily,
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          letterSpacing: '-0.01em',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {label}
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Button
                  component={RouterLink}
                  to="/security"
                  endIcon={<ArrowForwardRounded sx={{ fontSize: '1.05rem' }} />}
                  sx={{
                    textTransform: 'none',
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    borderRadius: '999px',
                    px: 2.75,
                    py: 1,
                    color: theme.palette.text.primary,
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.18)}`,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.text.primary, 0.04),
                    },
                  }}
                >
                  View security overview
                </Button>
              </Box>
            </AnimateOnScroll>

            {/* RIGHT — magnified 2x2 compliance grid with dashed dividers. */}
            <AnimateOnScroll
              isVisible={isVisible('security-grid')}
              delay={0.1}
              data-animate-id="security-grid"
            >
              <Box
                sx={{
                  borderRadius: 2,
                  border: `1px dashed ${alpha(theme.palette.text.primary, 0.16)}`,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  overflow: 'hidden',
                }}
              >
                {[
                  // Top row: frameworks we are aligned with (label only — being
                  // aligned is self-evident, so no status badge). Bottom row:
                  // the in-progress certifications, which DO carry a badge.
                  { label: 'GDPR', Icon: PolicyRounded, status: '', done: true },
                  { label: 'CCPA', Icon: GavelRounded, status: '', done: true },
                  { label: 'SOC 2', Icon: VerifiedUserIcon, status: 'In progress', done: false },
                  { label: 'ISO 27001', Icon: PublicRounded, status: 'In progress', done: false },
                ].map(({ label, Icon, status, done }, i) => (
                  <Box
                    key={label}
                    sx={{
                      minHeight: { xs: 150, md: 184 },
                      p: { xs: 2.5, md: 3 },
                      display: 'flex',
                      flexDirection: 'column',
                      borderRight:
                        i % 2 === 0
                          ? `1px dashed ${alpha(theme.palette.text.primary, 0.16)}`
                          : 'none',
                      borderBottom:
                        i < 2
                          ? `1px dashed ${alpha(theme.palette.text.primary, 0.16)}`
                          : 'none',
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: { xs: 46, md: 58 },
                          color: alpha(theme.palette.text.primary, 0.5),
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontFamily: theme.typography.fontFamily,
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          letterSpacing: '-0.01em',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {label}
                      </Box>
                      {!done && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              backgroundColor: '#D4694E',
                              flexShrink: 0,
                            }}
                          />
                          <Box
                            component="span"
                            sx={{
                              fontFamily: theme.typography.fontFamily,
                              fontSize: '0.68rem',
                              fontWeight: 500,
                              color: '#B83B1D',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {status}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </AnimateOnScroll>
          </Box>
        </Container>
      </Section>

      {/* CLOSING CTA BAND — "See it on your own deck". Sits just above the
          Pricing section so the demo ask leads directly into pricing. Warm
          gradient + top hairline so it reads as a deliberate band. */}
      <Box
        sx={{
          position: 'relative',
          borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          background: `radial-gradient(ellipse 70% 120% at 50% 0%, ${alpha('#B83B1D', 0.06)} 0%, transparent 60%)`,
          px: 2,
          py: { xs: 7, md: 10 },
        }}
      >
        <AnimateOnScroll
          isVisible={isVisible('closing-cta')}
          data-animate-id="closing-cta"
        >
          <Box sx={{ maxWidth: 680, mx: 'auto', textAlign: 'center' }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontSize: { xs: '1.9rem', md: '2.6rem' },
                fontWeight: 600,
                letterSpacing: '-0.03em',
                lineHeight: 1.08,
                color: theme.palette.text.primary,
                mb: 1.75,
              }}
            >
              See it on your{' '}
              <Box
                component="em"
                sx={{
                  fontStyle: 'italic',
                  color: '#B83B1D',
                  fontFamily: BRAND_SANS_FONT,
                }}
              >
                own work.
              </Box>
            </Typography>
            {/* Proof-run mechanics, stated explicitly: the entire ask an
                approving partner has to say yes to. Pre-answers the two
                documented blockers, effort and data governance. */}
            <Box
              sx={{
                maxWidth: 560,
                mx: 'auto',
                mb: 3.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                textAlign: 'left',
              }}
            >
              {[
                'Send one recurring deliverable - a board pack, IC deck, or model. If sharing is a problem, we build a representative sample instead: you share nothing.',
                'We rebuild it live on your template, in Office, on a 15-minute call.',
                'You judge the output. No install, no client data, no commitment.',
              ].map((step, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 22,
                      height: 22,
                      mt: '2px',
                      borderRadius: '50%',
                      backgroundColor: alpha('#B83B1D', 0.12),
                      color: '#B83B1D',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: theme.typography.fontFamily,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: theme.typography.fontFamily,
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      fontWeight: 400,
                      lineHeight: 1.55,
                      color: alpha(theme.palette.text.primary, 0.66),
                    }}
                  >
                    {step}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                justifyContent: 'center',
              }}
            >
              <Button
                component={RouterLink}
                to="/demo"
                endIcon={<ArrowForwardRounded sx={{ fontSize: '1.05rem' }} />}
                sx={{
                  textTransform: 'none',
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  borderRadius: '999px',
                  px: 3.25,
                  py: 1.15,
                  backgroundColor: '#2E140A',
                  color: '#FAF9F7',
                  boxShadow: '0 4px 14px rgba(46, 20, 10, 0.18)',
                  '&:hover': {
                    backgroundColor: '#1A0A04',
                    boxShadow: '0 6px 18px rgba(46, 20, 10, 0.28)',
                  },
                }}
              >
                See it on your own deck
              </Button>
              <Button
                component={RouterLink}
                to="/product"
                sx={{
                  textTransform: 'none',
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  borderRadius: '999px',
                  px: 3.25,
                  py: 1.15,
                  color: theme.palette.text.primary,
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.18)}`,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.text.primary, 0.04),
                  },
                }}
              >
                Watch the 1-minute demo
              </Button>
            </Box>
          </Box>
        </AnimateOnScroll>
      </Box>

      {/* PRICING */}
      <Section id="pricing">
        <Container>
          {/* Two-panel layout mirroring #security: left = the price narrative
              + wedge + CTAs; right = a magnified 2x2 value grid. The granular
              tier/credit table lives on /pricing so the landing stays
              high-level. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '0.95fr 1.05fr' },
              gap: { xs: 5, md: 8 },
              alignItems: 'center',
            }}
          >
            {/* LEFT — narrative + CTAs */}
            <AnimateOnScroll
              isVisible={isVisible('pricing-header')}
              data-animate-id="pricing-header"
              sx={{ gridColumn: { md: 1 } }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 1.5,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `1px solid ${alpha(theme.palette.text.primary, 0.14)}`,
                      backgroundColor: alpha(theme.palette.text.primary, 0.03),
                      color: '#B83B1D',
                    }}
                  >
                    <PaidRounded sx={{ fontSize: 16 }} />
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontFamily: theme.typography.fontFamily,
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      color: '#B83B1D',
                    }}
                  >
                    Pricing
                  </Box>
                </Box>

                <SectionTitle component="h2" sx={{ textAlign: 'left', mb: { xs: 2.5, md: 3 } }}>
                  Priced below a single <em>analyst week</em>.
                </SectionTitle>

                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    fontWeight: 500,
                    lineHeight: 1.55,
                    color: alpha(theme.palette.text.primary, 0.66),
                    mb: { xs: 3, md: 3.5 },
                    maxWidth: 460,
                    mx: { xs: 'auto', md: 0 },
                  }}
                >
                  One price per seat, billed monthly to your engagement code. Credits pool across the team and all three apps.
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'flex-start' }}>
                  <Button
                    component={RouterLink}
                    to="/demo"
                    sx={{
                      textTransform: 'none',
                      fontFamily: theme.typography.fontFamily,
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      letterSpacing: '-0.005em',
                      borderRadius: '999px',
                      px: 3,
                      py: 1.1,
                      color: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff',
                      backgroundColor: theme.palette.mode === 'dark' ? '#ffffff' : '#2E140A',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? '#f0f0f0' : '#1A0A04',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                      },
                    }}
                  >
                    See it on your own deck
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/pricing"
                    endIcon={<ArrowForwardRounded sx={{ fontSize: '1.05rem' }} />}
                    sx={{
                      textTransform: 'none',
                      fontFamily: theme.typography.fontFamily,
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      borderRadius: '999px',
                      px: 2.75,
                      py: 1,
                      color: theme.palette.text.primary,
                      border: `1px solid ${alpha(theme.palette.text.primary, 0.18)}`,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.text.primary, 0.04),
                      },
                    }}
                  >
                    See full pricing
                  </Button>
                </Box>
              </Box>
            </AnimateOnScroll>

            {/* RIGHT — magnified 2x2 value grid with dashed dividers. */}
            <AnimateOnScroll
              isVisible={isVisible('pricing-grid')}
              delay={0.1}
              data-animate-id="pricing-grid"
              sx={{ gridColumn: { md: 2 }, gridRow: { md: 1 } }}
            >
              <Box
                sx={{
                  borderRadius: 2,
                  border: `1px dashed ${alpha(theme.palette.text.primary, 0.16)}`,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  overflow: 'hidden',
                }}
              >
                {[
                  { Icon: PaidRounded, label: 'From $800/mo', sub: 'Billed monthly' },
                  { Icon: ReceiptLongRounded, label: 'Engagement code', sub: 'Billed monthly to your engagement code' },
                  { Icon: BoltRounded, label: 'Pooled credits', sub: 'Shared across the team and all three apps' },
                  { Icon: LockOpenRounded, label: 'No lock-in', sub: 'Cancel anytime' },
                ].map(({ Icon, label, sub }, i) => (
                  <Box
                    key={label}
                    sx={{
                      minHeight: { xs: 150, md: 184 },
                      p: { xs: 2.5, md: 3 },
                      display: 'flex',
                      flexDirection: 'column',
                      borderRight:
                        i % 2 === 0
                          ? `1px dashed ${alpha(theme.palette.text.primary, 0.16)}`
                          : 'none',
                      borderBottom:
                        i < 2
                          ? `1px dashed ${alpha(theme.palette.text.primary, 0.16)}`
                          : 'none',
                    }}
                  >
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon sx={{ fontSize: { xs: 42, md: 54 }, color: alpha(theme.palette.text.primary, 0.5) }} />
                    </Box>
                    <Box>
                      <Box
                        component="span"
                        sx={{
                          display: 'block',
                          fontFamily: theme.typography.fontFamily,
                          fontSize: '0.92rem',
                          fontWeight: 700,
                          letterSpacing: '-0.01em',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {label}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          display: 'block',
                          fontFamily: theme.typography.fontFamily,
                          fontSize: '0.72rem',
                          fontWeight: 500,
                          color: alpha(theme.palette.text.primary, 0.55),
                          mt: 0.25,
                        }}
                      >
                        {sub}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </AnimateOnScroll>
          </Box>

        </Container>
      </Section>

      {/* The standalone "Built for Trust" section was folded into the
          Pricing section above as an inline trust-signals row, and the
          "Price Locked at Signup" card was retired (it overlapped with
          the Design Partner banner's "locked-in rates"). */}

      {/* FAQ: the objections a deal team raises before it will trial
          anything, answered on the page instead of only inside the support
          widget. Copy is reused verbatim from FAQ_REPOSITORY (the same
          answers the widget serves) so there is one source of truth, and
          the refund entry carries the risk reversal. Emitted as FAQPage
          JSON-LD below for answer engines. */}
      <Section id="faq">
        <Container maxWidth="lg">
          <AnimateOnScroll isVisible={isVisible('faq-header')} data-animate-id="faq-header">
            <CenteredHeader>
              <SectionEyebrow>Questions</SectionEyebrow>
              <SectionTitle component="h2">Before you <em>trial it</em>.</SectionTitle>
            </CenteredHeader>
          </AnimateOnScroll>
          <AnimateOnScroll isVisible={isVisible('faq-list')} data-animate-id="faq-list">
            <Box sx={{ maxWidth: 760, mx: 'auto' }}>
              {LANDING_FAQ.map(({ question, answer }) => (
                <Accordion
                  key={question}
                  disableGutters
                  elevation={0}
                  square
                  sx={{
                    backgroundColor: 'transparent',
                    borderBottom: `1px solid ${alpha('#2E140A', 0.08)}`,
                    '&::before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreRounded sx={{ color: alpha('#2E140A', 0.45) }} />}
                    sx={{
                      px: 0.5,
                      minHeight: 52,
                      '& .MuiAccordionSummary-content': { my: 1 },
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: theme.typography.fontFamily,
                        fontSize: '0.92rem',
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        color: 'text.primary',
                      }}
                    >
                      {question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 2 }}>
                    <Typography
                      sx={{
                        fontFamily: theme.typography.fontFamily,
                        fontSize: '0.84rem',
                        lineHeight: 1.6,
                        color: 'text.secondary',
                      }}
                    >
                      {answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </AnimateOnScroll>
        </Container>
      </Section>

      {/* Blog / Insights — "Our thinking" section. Cards link to the full
          posts; "View all posts" opens the /blog index. */}
      <Section id="blog">
        <Container maxWidth="lg">
          <AnimateOnScroll
            isVisible={isVisible('blog-header')}
            data-animate-id="blog-header"
          >
            <CenteredHeader>
              <SectionEyebrow>Newsroom</SectionEyebrow>
              <SectionTitle component="h2">What&rsquo;s <em>new</em></SectionTitle>
              <SectionSubtitle>
                Product launches, partnerships, and writing on AI for Private Equity, the deck-prep workflow, and where PE software is heading.
              </SectionSubtitle>
            </CenteredHeader>
          </AnimateOnScroll>

          <BlogGrid>
            <AnimateOnScroll
              isVisible={isVisible('blog-0')}
              delay={0.1}
              data-animate-id="blog-0"
            >
              <BlogCard as="a" href="/blog/brand-guidelines" onClick={handleOpenBlog('brand-guidelines')}>
                <BlogCategory>Product</BlogCategory>
                <BlogTitle className="blog-title">Why your AI slide tool ignores your brand guidelines</BlogTitle>
                <BlogMeta>
                  <span>4 min</span>
                  <BlogReadMore className="blog-read-more">Read &rarr;</BlogReadMore>
                </BlogMeta>
                <BlogExcerpt>Most AI presentation tools generate slides in a vacuum: no slide master, no brand RGB codes, hours of reformatting.</BlogExcerpt>
              </BlogCard>
            </AnimateOnScroll>

            <AnimateOnScroll
              isVisible={isVisible('blog-1')}
              delay={0.15}
              data-animate-id="blog-1"
            >
              <BlogCard as="a" href="/blog/cost-of-powerpoint" onClick={handleOpenBlog('cost-of-powerpoint')}>
                <BlogCategory>Industry</BlogCategory>
                <BlogTitle className="blog-title">The real cost of PowerPoint in professional services</BlogTitle>
                <BlogMeta>
                  <span>6 min</span>
                  <BlogReadMore className="blog-read-more">Read &rarr;</BlogReadMore>
                </BlogMeta>
                <BlogExcerpt>Associates spend 20–40 hours a week in PowerPoint. At $150K loaded cost, that's $75–150 an hour on formatting, not thinking.</BlogExcerpt>
              </BlogCard>
            </AnimateOnScroll>

            <AnimateOnScroll
              isVisible={isVisible('blog-2')}
              delay={0.2}
              data-animate-id="blog-2"
            >
              <BlogCard as="a" href="/blog/pe-software-led" onClick={handleOpenBlog('pe-software-led')}>
                <BlogCategory>Industry</BlogCategory>
                <BlogTitle className="blog-title">Half the PE industry calls itself software-led now</BlogTitle>
                <BlogMeta>
                  <span>4 min</span>
                  <BlogReadMore className="blog-read-more">Read &rarr;</BlogReadMore>
                </BlogMeta>
                <BlogExcerpt>The stickiness was mostly friction: bad integrations, lock-in, data trapped in corners. AI eats that first, and the future looks K-shaped.</BlogExcerpt>
              </BlogCard>
            </AnimateOnScroll>
          </BlogGrid>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 4, md: 5 } }}>
            <Box
              component={RouterLink}
              to="/news"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.6,
                fontFamily: theme.typography.fontFamily,
                fontSize: '0.875rem',
                fontWeight: 500,
                letterSpacing: '-0.005em',
                color: '#B83B1D',
                textDecoration: 'none',
                transition: 'gap 0.2s ease',
                '&:hover': { gap: 1 },
              }}
            >
              View all news &rarr;
            </Box>
          </Box>
        </Container>
      </Section>

      {/* Closing CTA band moved to sit just above the Pricing section. */}

      {/* AI TRANSFORMATION STRIP. Deliberately the last thing before the
          footer, and deliberately small. A reader who got this far and wants
          AI working across the business rather than one tool has told us which
          offer they are, and this is where they can say so. It reads as a second service
          line, not a pivot: anything louder or higher up would tell a fund
          reviewer that the product is a demo attached to a consultancy, which
          is the opposite of the story the rest of the page tells. */}
      <Section>
        <Container>
          <AnimateOnScroll
            isVisible={isVisible('advisory-strip')}
            data-animate-id="advisory-strip"
          >
            <Box
              component={RouterLink}
              to="/ai-transformation"
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: { xs: 2, md: 4 },
                maxWidth: 940,
                mx: 'auto',
                p: { xs: 3, md: 3.5 },
                borderRadius: '16px',
                border: `1px solid ${alpha('#2E140A', 0.1)}`,
                backgroundColor: alpha('#ffffff', 0.55),
                textDecoration: 'none',
                transition: 'border-color 0.22s ease, box-shadow 0.22s ease, transform 0.22s cubic-bezier(0.16,1,0.3,1)',
                '&:hover': {
                  borderColor: alpha('#B83B1D', 0.3),
                  boxShadow: `0 12px 30px ${alpha('#2E140A', 0.07)}`,
                  transform: 'translateY(-2px)',
                },
                '&:hover .advisory-strip__go': { gap: 1 },
                '&:focus-visible': { outline: '2px solid #B83B1D', outlineOffset: 3 },
                '@media (prefers-reduced-motion: reduce)': {
                  transition: 'none',
                  '&:hover': { transform: 'none' },
                },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  component="p"
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontSize: { xs: '1rem', md: '1.05rem' },
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                    color: theme.palette.text.primary,
                    mb: 0.75,
                  }}
                >
                  Need AI in daily work, not another pilot?
                </Typography>
                <Typography
                  component="p"
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontSize: { xs: '0.875rem', md: '0.92rem' },
                    lineHeight: 1.6,
                    color: theme.palette.text.secondary,
                  }}
                >
                  Restack picks the workflows where AI pays, builds AI agents into the tools
                  your teams already use, and gets people using them.
                </Typography>
              </Box>
              <Box
                className="advisory-strip__go"
                sx={{
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.6,
                  fontFamily: theme.typography.fontFamily,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  letterSpacing: '-0.005em',
                  color: '#B83B1D',
                  transition: 'gap 0.2s ease',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                }}
              >
                AI Transformation &rarr;
              </Box>
            </Box>
          </AnimateOnScroll>
        </Container>
      </Section>

      {/* Footer */}
      <Footer />

      {/* FAQ Chat Widget - Customer Support */}
      <FAQChatWidget isDarkMode={false} />

      {/* Blog article lightbox — hash-synced, back-button friendly */}
      <BlogPostModal slug={openBlogSlug} onClose={handleCloseBlog} />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%', borderRadius: '12px' }}>
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Sign-in Dialog */}
      <Dialog
        open={signInData.signInOpen}
        onClose={!signInData.signInLoading ? signInData.handleCloseSignIn : undefined}
        className="sign-in-dialog"
        PaperProps={{
          sx: {
            backgroundColor: `${theme.palette.background.paper} !important`,
            width: "340px",
            maxWidth: "90vw",
            borderRadius: 2,
            p: 3,
            pt: 4,
            boxShadow: "0 25px 60px rgba(0,0,0,0.15)",
            position: "relative",
            overflow: "visible",
          },
        }}
      >
        <IconButton
          onClick={signInData.handleCloseSignIn}
          disabled={signInData.signInLoading}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
            width: 32,
            height: 32,
            "&:hover": {
              backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Box sx={{ textAlign: "center", mb: 3 }}>
          <brandConfig.icon sx={{ fontSize: 32, color: 'text.primary', mb: 2 }} />
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {signInData.emailMode === 'reset' ? 'Reset password' : signInData.emailMode === 'signup' ? 'Create account' : 'Welcome to Restack'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {signInData.emailMode === 'reset' ? 'Enter your email to receive a reset link.' : 'Sign in to manage your subscription'}
          </Typography>
        </Box>

        {signInData.signInError && (
          <Alert
            severity={signInData.signInError.includes('Reset link sent') ? 'success' : 'error'}
            onClose={() => signInData.setSignInError?.(null)}
            sx={{ mb: 2 }}
          >
            {signInData.signInError}
          </Alert>
        )}

        {/* Email form (primary) */}
        <Box component="form" onSubmit={signInData.handleEmailSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {signInData.emailMode === 'signup' && (
            <TextField size="small" placeholder="Full name" value={signInData.nameInput} onChange={(e) => signInData.setNameInput(e.target.value)} fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', fontSize: '0.85rem' } }} />
          )}
          <TextField size="small" type="email" placeholder="Email" value={signInData.emailInput} onChange={(e) => signInData.setEmailInput(e.target.value)} required fullWidth
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', fontSize: '0.85rem' } }} />
          {signInData.emailMode !== 'reset' && (
            <TextField size="small" type="password" placeholder="Password" value={signInData.passwordInput} onChange={(e) => signInData.setPasswordInput(e.target.value)} required fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', fontSize: '0.85rem' } }} />
          )}
          <Button type="submit" variant="contained" disabled={signInData.emailLoading} fullWidth
            sx={{
              borderRadius: '999px', textTransform: 'none', fontWeight: 600, fontSize: '0.9rem', py: 1.2,
              backgroundColor: theme.palette.mode === 'dark' ? '#ffffff' : '#2E140A',
              color: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff',
              boxShadow: 'none',
              '&:hover': { backgroundColor: theme.palette.mode === 'dark' ? '#f0f0f0' : '#1A0A04' },
            }}>
            {signInData.emailLoading ? <CircularProgress size={18} color="inherit" /> :
              signInData.emailMode === 'reset' ? 'Send reset link' :
              signInData.emailMode === 'signup' ? 'Create account' : 'Sign in'}
          </Button>
        </Box>

        {/* Divider */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2 }}>
          <Divider sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">or</Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>

        {/* Microsoft sign-in (primary SSO option for a PowerPoint plugin) */}
        <Button
          fullWidth
          variant="outlined"
          onClick={signInData.handleMicrosoftSignIn}
          disabled={signInData.signInLoading}
          startIcon={
            signInData.signInLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <svg width="16" height="16" viewBox="0 0 23 23">
                <path fill="#f25022" d="M1 1h10v10H1z"/>
                <path fill="#00a4ef" d="M1 12h10v10H1z"/>
                <path fill="#7fba00" d="M12 1h10v10H12z"/>
                <path fill="#ffb900" d="M12 12h10v10H12z"/>
              </svg>
            )
          }
          sx={{
            py: 1,
            mb: 1,
            borderRadius: '999px',
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.85rem",
            color: "text.secondary",
          }}
        >
          {signInData.signInLoading ? "Signing in..." : "Continue with Microsoft"}
        </Button>

        {/* Toggle links */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1.5 }}>
          {signInData.emailMode === 'signin' && (
            <>
              <Link component="button" variant="caption" underline="hover" color="text.secondary" onClick={() => { signInData.setEmailMode('signup'); signInData.setSignInError(null); }}>Create account</Link>
              <Link component="button" variant="caption" underline="hover" color="text.secondary" onClick={() => { signInData.setEmailMode('reset'); signInData.setSignInError(null); }}>Forgot password?</Link>
            </>
          )}
          {(signInData.emailMode === 'signup' || signInData.emailMode === 'reset') && (
            <Link component="button" variant="caption" underline="hover" color="text.secondary" onClick={() => { signInData.setEmailMode('signin'); signInData.setSignInError(null); }}>Back to sign in</Link>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 2, fontSize: "0.7rem", lineHeight: 1.5 }}>
          By signing in, you agree to our{' '}
          <Link href="/terms" underline="hover" sx={{ color: 'text.secondary' }}>Terms</Link>{' '}
          and{' '}
          <Link href="/privacy" underline="hover" sx={{ color: 'text.secondary' }}>Privacy Policy</Link>
        </Typography>
      </Dialog>

    </LandingContainer>
  );
};

export default HomePage;
